import { bindActionCreators } from "redux";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { hasCustomDynamicHeader } from "app/reducers/brandSelector";
import { getPartnerMarketing } from "app/reducers/partnerSelector";
import { useContext, useEffect, useMemo } from "react";
import { clearBooking } from "app/pages/Booking/bookingActionCreators";
import PropTypes from "prop-types";
import HomeLayout from "app/pages/HomeLayout/HomeLayout";
import { clearSmartDP } from "app/pages/SmartDP/smartDPActionCreators";
import {
	getActiveHeroCommercialMerchCode,
	getHeroCommercialCarouselMerchandisings,
} from "app/pages/Merchandising/merchandisingSelector";
import AppGlobalsContext from "app/AppGlobalsContext";
import { useHeroCommercialMerchandising } from "app/features/heroCommercial";
import { fetchAllStopoverCities } from "app/pages/Stopover/stopoverActionCreators";
import { WEBSITE_TUNNEL_TYPES } from "app/constants";
import { fetchTunnelSearchFilters } from "app/pages/.shared/WebsiteTunnelSwitcher/tunnelSearchFiltersActionCreators";

const EMPTY_HERO_COMMERCIAL_MERCH = {};

const HomeLayoutContainer = ownProps => {
	const stateProps = useSelector(mapStateToProps, shallowEqual);
	const dispatch = useDispatch();
	const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);
	const props = { ...ownProps, ...stateProps, ...dispatchProps };
	const { shop, partnerCode, productsVisibility, brand } = useContext(AppGlobalsContext);
	const { heroCommercialCode, fetchAllStopoverCities, websiteTunnels } = props;
	const heroCommercialQuery = useHeroCommercialMerchandising({
		code: heroCommercialCode,
		shop,
		productsVisibility,
		partnerCode,
	});
	const heroCommercialMerch = heroCommercialQuery.data ?? EMPTY_HERO_COMMERCIAL_MERCH;
	const locale = shop.slice(0, 2);
	const hasStopoverTunnel = websiteTunnels?.some(
		tunnel => tunnel?.websiteTunnelType === WEBSITE_TUNNEL_TYPES.STOPOVER
	);
	// Le premier onglet est celui qu'affiche WebsiteTunnelSwitcher, qui charge les autres au clic.
	const defaultTunnelType = websiteTunnels?.[0]?.websiteTunnelType;

	useEffect(() => {
		props.clearSmartDP();
		props.clearBooking();
	}, []);

	useEffect(() => {
		if (hasStopoverTunnel) {
			fetchAllStopoverCities({ brand, locale });
		}

		const searchFiltersAction = fetchTunnelSearchFilters({
			websiteTunnelType: defaultTunnelType,
		});

		if (searchFiltersAction) {
			dispatch(searchFiltersAction);
		}
	}, [brand, locale, hasStopoverTunnel, defaultTunnelType]);

	return <HomeLayout {...props} heroCommercialMerch={heroCommercialMerch} />;
};

const mapStateToProps = state => {
	const marketing = getPartnerMarketing(state);
	return {
		isBrandWithCustomHeader: hasCustomDynamicHeader(state),
		isTransparentHeader: marketing?.isTransparentSdpSearchHeader || false,
		heroCommercialCode: getActiveHeroCommercialMerchCode(state),
		brandCode: state.brand?.code,
		carouselMerchandisings: getHeroCommercialCarouselMerchandisings(state),
		destinations: state.smartDP?.destinations,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			clearBooking,
			clearSmartDP,
			fetchAllStopoverCities,
		},
		dispatch
	);
};

HomeLayoutContainer.propTypes = {
	clearBooking: PropTypes.func,
	clearSmartDP: PropTypes.func,
	heroCommercialCode: PropTypes.string,
	fetchAllStopoverCities: PropTypes.func,
	websiteTunnels: PropTypes.array,
	carouselMerchandisings: PropTypes.array,
};

export default HomeLayoutContainer;
